/* eslint-disable react/no-unescaped-entities */
import { Lock, ArrowDown } from 'lucide-react';
import LegalPageLayout from './LegalPageLayout';

/**
 * Politique de confidentialité — RGPD-compliant. Targets the actual Hérita
 * data flow (ephemeral document upload → LLM analysis → automatic file
 * deletion → MongoDB analysis row keyed by file hash). Wording is honest
 * about the third-party LLM (Google Gemini via Emergent integrations).
 */
const CONTACT_EMAIL = 'helloherita@proton.me';

const COMMITMENTS = [
  'Votre document original est supprimé automatiquement dès la fin de l\'analyse.',
  'Vos documents ne sont jamais revendus ni utilisés à des fins publicitaires.',
  'Vos données ne servent jamais à entraîner un modèle d\'intelligence artificielle.',
  'Seule l\'analyse générée est conservée afin que vous puissiez retrouver votre dossier.',
  'Vous pouvez demander la suppression de votre analyse à tout moment.',
];

const LIFECYCLE = [
  'Vous importez votre document',
  'Analyse sécurisée',
  'Création de votre dossier personnalisé',
  'Suppression automatique du document original',
  'Seule votre analyse reste accessible',
];

const Confidentialite = () => {
  return (
    <LegalPageLayout
      eyebrow="Politique de confidentialité"
      title="Vos documents vous appartiennent. Nos engagements, en clair."
      lastUpdated="1ᵉʳ juin 2026"
      testId="page-confidentialite"
      seoTitle="Politique de confidentialité | HÉRITA"
      seoDescription="Politique de confidentialité Hérita : traitement des données, RGPD, suppression des documents après analyse et droits des utilisateurs."
      seoPath="/confidentialite"
    >
      <p>
        Hérita propose un service d'analyse de documents notariés et
        patrimoniaux français (succession, donation, vente immobilière,
        testament, indivision, SCI, etc.). Cette politique décrit, en toute
        transparence, les données que nous traitons, pourquoi, pendant combien
        de temps, et les droits dont vous disposez en application du Règlement
        général sur la protection des données (RGPD) et de la loi française
        « Informatique et Libertés ».
      </p>

      <div className="legal-callout sand">
        <p style={{ marginBottom: 0 }}>
          <strong>En une phrase :</strong> nous utilisons vos documents
          uniquement pour produire l'analyse que vous demandez. Ils ne sont
          jamais revendus, jamais exploités à d'autres fins, et sont
          automatiquement supprimés de nos serveurs immédiatement après le
          traitement.
        </p>
      </div>

      {/* ─────────────────────────────────────────────
          Engagements visuels — lecture en < 30 s
          ───────────────────────────────────────────── */}
      <section
        className="my-12 sm:my-14"
        aria-labelledby="engagements-heading"
        data-testid="privacy-commitments"
      >
        <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] opacity-70 mb-3 text-center">
          Nos engagements
        </p>
        <div
          id="engagements-heading"
          role="heading"
          aria-level={2}
          className="font-['Cormorant_Garamond'] font-light text-[28px] sm:text-[34px] text-[#1A2530] mb-8 sm:mb-10 leading-[1.15] text-center"
          style={{ letterSpacing: '-0.01em' }}
        >
          Nos engagements en un coup d'œil
        </div>
        <div role="list" className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {COMMITMENTS.map((line, idx) => (
            <div
              role="listitem"
              key={idx}
              className={`flex items-start gap-3.5 bg-white border border-[#E2DBCF] rounded-2xl p-5 sm:p-6 ${
                idx === COMMITMENTS.length - 1 ? 'sm:col-span-2' : ''
              }`}
              data-testid={`commitment-${idx}`}
            >
              <span className="mt-0.5 w-9 h-9 rounded-full bg-[#E3D5CA] flex items-center justify-center flex-shrink-0">
                <Lock className="h-4 w-4 text-[#1A2530]" aria-hidden="true" />
              </span>
              <span className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#1A2530] leading-snug pt-1.5">
                {line}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          Frise : cycle de vie d'un document
          ───────────────────────────────────────────── */}
      <section
        className="my-12 sm:my-14"
        aria-labelledby="lifecycle-heading"
        data-testid="privacy-lifecycle"
      >
        <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#2C363F] opacity-70 mb-3 text-center">
          Cycle de vie
        </p>
        <div
          id="lifecycle-heading"
          role="heading"
          aria-level={2}
          className="font-['Cormorant_Garamond'] font-light text-[28px] sm:text-[34px] text-[#1A2530] mb-10 sm:mb-12 leading-[1.15] text-center"
          style={{ letterSpacing: '-0.01em' }}
        >
          Que devient votre document, étape par étape
        </div>
        <div role="list" className="flex flex-col items-center gap-0">
          {LIFECYCLE.map((step, idx) => (
            <div role="listitem" key={idx} className="flex flex-col items-center w-full max-w-md">
              <div
                className="w-full bg-white border border-[#E2DBCF] rounded-2xl px-5 py-4 sm:py-5 flex items-center gap-4"
                data-testid={`lifecycle-step-${idx}`}
              >
                <span className="font-['IBM_Plex_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#2C363F] opacity-70 flex-shrink-0 w-7">
                  0{idx + 1}
                </span>
                <span className="font-['Cormorant_Garamond'] text-[18px] sm:text-[21px] text-[#1A2530] leading-tight">
                  {step}
                </span>
              </div>
              {idx < LIFECYCLE.length - 1 && (
                <ArrowDown
                  className="h-4 w-4 text-[#1A2530] opacity-50 my-2.5 sm:my-3"
                  aria-hidden="true"
                />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ─────────────────────────────────────────────
          Encart — pourquoi votre analyse reste accessible
          ───────────────────────────────────────────── */}
      <aside
        className="my-12 sm:my-14 bg-white border-t-2 border-t-[#1A2530] border-x border-b border-[#E2DBCF] rounded-2xl p-6 sm:p-8"
        data-testid="privacy-why-accessible"
      >
        <p className="font-['IBM_Plex_Mono'] text-[10px] sm:text-[11px] uppercase tracking-[0.28em] text-[#1A2530] mb-3">
          Bon à savoir
        </p>
        <div
          role="heading"
          aria-level={3}
          className="font-['Cormorant_Garamond'] font-medium text-[22px] sm:text-[26px] text-[#1A2530] mb-4 leading-tight"
        >
          Pourquoi pouvez-vous retrouver votre analyse si votre document est
          supprimé&nbsp;?
        </div>
        <p className="font-['Work_Sans'] text-[14.5px] sm:text-[15px] text-[#2C363F] leading-relaxed m-0">
          Une fois le document analysé, HÉRITA n'a plus besoin de conserver le
          fichier original. Seule l'analyse générée est enregistrée afin de
          vous permettre de consulter à nouveau votre dossier sans avoir à
          réimporter votre document.
        </p>
      </aside>

      <h2>1. Responsable du traitement</h2>
      <p>
        Le responsable du traitement des données collectées via le Service est
        Hérita, dont les coordonnées figurent dans les{' '}
        <a href="/mentions-legales">mentions légales</a>. Pour toute question
        relative à vos données personnelles, vous pouvez écrire à{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>2. Données que nous collectons</h2>

      <h3>Documents que vous nous transmettez</h3>
      <p>
        Lorsque vous téléversez un document (acte notarié, déclaration de
        succession, courrier fiscal, etc.), son contenu intégral est temporairement
        stocké sur nos serveurs le temps nécessaire à son analyse, puis supprimé
        immédiatement à l'issue du traitement. Aucune copie persistante du
        fichier n'est conservée par Hérita.
      </p>

      <h3>Analyse structurée produite par le Service</h3>
      <p>
        Le résultat de l'analyse (catégorisation du document, éléments
        identifiés, lecture personnalisée, etc.) est conservé dans notre base
        de données. Il est associé à une empreinte cryptographique (hash) du
        document d'origine, qui permet de servir instantanément la même analyse
        si vous soumettez exactement le même fichier, sans réexécuter le
        traitement.
      </p>

      <h3>Données techniques</h3>
      <p>
        Pour assurer le bon fonctionnement et la sécurité du Service, nous
        pouvons collecter des journaux techniques (date et heure d'utilisation,
        adresse IP, type de navigateur, page consultée, erreurs éventuelles).
        Ces journaux sont conservés pendant une durée limitée et ne sont
        utilisés qu'à des fins de diagnostic, de sécurité et de prévention des
        abus.
      </p>

      <h3>Données de contact que vous nous transmettez volontairement</h3>
      <p>
        Si vous nous écrivez (via la <a href="/contact">page Contact</a> ou par
        courriel), nous traitons uniquement les informations que vous décidez
        de partager (adresse e-mail, contenu du message), strictement pour
        répondre à votre demande.
      </p>

      <h3>Diagnostic gratuit (page <a href="/diagnostic">/diagnostic</a>)</h3>
      <p>
        Si vous utilisez le diagnostic gratuit, vous nous transmettez
        volontairement des informations non nominatives sur votre situation :
        besoin principal (succession, donation, protection du conjoint…),
        situation familiale (statut matrimonial, nombre d'enfants, conjoint
        survivant, famille recomposée), patrimoine approximatif (présence
        d'une résidence principale, autres biens, fourchette d'épargne,
        assurance-vie, dettes), documents ou décisions existantes
        (testament, donation, donation-partage, acte notarié reçu).
      </p>
      <p>
        Ces réponses sont utilisées <strong>uniquement</strong> pour générer
        votre diagnostic personnalisé à l'écran. Elles ne sont
        <strong>jamais</strong> exposées à un modèle d'intelligence
        artificielle (le diagnostic est calculé par notre propre moteur
        de règles déterministe).
      </p>
      <p>
        Si vous demandez à recevoir votre diagnostic par e-mail, nous
        collectons votre adresse e-mail pour vous l'envoyer via notre
        prestataire d'envoi transactionnel (Resend). L'envoi du
        diagnostic constitue à lui seul votre consentement
        transactionnel ; aucune autre utilisation de votre e-mail
        n'est faite.
      </p>
      <p>
        L'inscription à nos futures actualités est une option{' '}
        <strong>séparée et non pré-cochée</strong> (champ{' '}
        <code>marketing_opt_in</code>). Si vous ne la cochez pas, votre
        e-mail ne sera utilisé que pour vous transmettre votre
        diagnostic et pour répondre à une éventuelle demande de votre
        part. Vous pouvez à tout moment retirer votre consentement
        marketing en nous écrivant à{' '}
        <a href="mailto:contact@herita.me">contact@herita.me</a>.
      </p>

      <h2>3. Finalités et bases légales</h2>
      <ul>
        <li>
          <strong>Fourniture du Service d'analyse</strong> (lecture du document,
          production d'un dossier personnalisé, export PDF) — base légale :
          exécution du contrat conclu avec vous (art. 6.1.b RGPD).
        </li>
        <li>
          <strong>Fourniture du diagnostic gratuit</strong> (calcul de la
          synthèse personnalisée, envoi par e-mail si demandé) — base
          légale : exécution d'une mesure pré-contractuelle prise à votre
          demande (art. 6.1.b RGPD).
        </li>
        <li>
          <strong>Information sur les futures évolutions d'Hérita</strong>{' '}
          (uniquement si vous avez explicitement coché la case{' '}
          <em>marketing_opt_in</em> sur la page diagnostic) — base
          légale : consentement (art. 6.1.a RGPD), révocable à tout
          moment.
        </li>
        <li>
          <strong>Amélioration et sécurisation du Service</strong> (journaux
          techniques, métriques anonymisées) — base légale : intérêt légitime
          d'Hérita à maintenir un service fiable et sûr (art. 6.1.f RGPD).
        </li>
        <li>
          <strong>Réponse à vos sollicitations</strong> (questions, exercice de
          vos droits RGPD) — base légale : exécution du contrat ou intérêt
          légitime.
        </li>
        <li>
          <strong>Respect des obligations légales</strong> (conservation de
          certaines traces de sécurité, prévention de la fraude) — base légale :
          obligation légale (art. 6.1.c RGPD).
        </li>
      </ul>
      <p>
        Vos documents et leurs analyses ne sont <strong>jamais</strong> revendus
        à des tiers, jamais utilisés à des fins de prospection commerciale, et
        jamais exploités pour entraîner un modèle d'intelligence artificielle.
      </p>

      <h2>4. Recours à un prestataire d'intelligence artificielle</h2>
      <p>
        Pendant quelques secondes, le contenu nécessaire à l'analyse est
        transmis à notre prestataire d'intelligence artificielle (modèles de
        la famille Google Gemini, accédés via une intégration sécurisée) afin
        de produire votre explication. Le document original est ensuite
        supprimé automatiquement de nos serveurs. Les données ne sont pas
        utilisées pour entraîner le modèle, conformément aux engagements
        contractuels du prestataire.
      </p>

      <h2>5. Durées de conservation</h2>
      <ul>
        <li>
          <strong>Document téléversé :</strong> supprimé immédiatement après le
          traitement (typiquement quelques secondes à quelques minutes).
        </li>
        <li>
          <strong>Analyse produite + empreinte du document :</strong> conservées
          tant que le Service est utilisé pour vous permettre de la consulter et
          d'éviter une nouvelle exécution si le même fichier vous est soumis.
          Vous pouvez à tout moment demander leur suppression.
        </li>
        <li>
          <strong>Journaux techniques :</strong> 12 mois maximum.
        </li>
        <li>
          <strong>Échanges par e-mail :</strong> conservés le temps nécessaire à
          la gestion de votre demande, puis archivés ou supprimés selon nos
          obligations légales.
        </li>
        <li>
          <strong>Diagnostic gratuit (réponses + e-mail) :</strong> conservés
          tant que vous n'avez pas demandé leur suppression ou retiré votre
          éventuel consentement marketing. Vous pouvez à tout moment nous
          écrire à{' '}
          <a href="mailto:contact@herita.me">contact@herita.me</a> pour
          obtenir l'effacement immédiat de votre lead et de vos réponses
          (droit à l'oubli, art. 17 RGPD).
        </li>
      </ul>

      <h2>6. Destinataires des données</h2>
      <p>
        Vos données ne sont accessibles qu'aux personnes habilitées au sein
        d'Hérita et, dans la stricte mesure nécessaire à la fourniture du
        Service, à nos sous-traitants techniques (hébergeur, prestataire
        d'intelligence artificielle, prestataire d'envoi d'e-mails). Ces
        sous-traitants sont sélectionnés en raison de leur conformité au RGPD et
        encadrés par des engagements contractuels écrits.
      </p>

      <h2>7. Transferts hors de l'Union européenne</h2>
      <p>
        Certains sous-traitants techniques (notamment le prestataire
        d'intelligence artificielle) peuvent traiter des données hors de l'Union
        européenne. Le cas échéant, ces transferts sont encadrés par les
        mécanismes prévus par le RGPD (clauses contractuelles types de la
        Commission européenne, garanties appropriées). Vous pouvez nous demander
        une copie de ces garanties par e-mail à{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>

      <h2>8. Sécurité</h2>
      <p>
        Hérita met en œuvre des mesures techniques et organisationnelles
        appropriées pour protéger vos données : chiffrement des connexions
        (HTTPS), accès restreint à la base de données, suppression automatisée
        des fichiers téléversés, journalisation des accès sensibles. Aucune
        mesure de sécurité ne pouvant garantir un risque nul, nous nous
        engageons à vous informer dans les meilleurs délais en cas d'incident
        susceptible d'affecter vos données.
      </p>

      <h2>9. Vos droits</h2>
      <p>
        Conformément au RGPD, vous disposez à tout moment des droits suivants :
      </p>
      <ul>
        <li>
          <strong>Droit d'accès</strong> aux données que nous détenons à votre sujet.
        </li>
        <li>
          <strong>Droit de rectification</strong> des données inexactes ou incomplètes.
        </li>
        <li>
          <strong>Droit à l'effacement</strong> de vos données (« droit à l'oubli »).
        </li>
        <li>
          <strong>Droit à la limitation</strong> du traitement dans certaines
          situations prévues par le RGPD.
        </li>
        <li>
          <strong>Droit d'opposition</strong>, pour des motifs légitimes, au
          traitement de vos données.
        </li>
        <li>
          <strong>Droit à la portabilité</strong> des données que vous nous avez
          fournies, dans un format structuré.
        </li>
        <li>
          <strong>Droit de définir des directives</strong> relatives au sort de
          vos données après votre décès.
        </li>
      </ul>
      <p>
        Pour exercer ces droits, écrivez-nous à{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> en précisant
        votre demande. Nous répondons sous un délai maximum d'un mois.
      </p>
      <p>
        Si vous estimez, après nous avoir contactés, que vos droits n'ont pas
        été respectés, vous pouvez introduire une réclamation auprès de la
        Commission nationale de l'informatique et des libertés (CNIL),{' '}
        <a href="https://www.cnil.fr/" target="_blank" rel="noopener noreferrer">
          www.cnil.fr
        </a>.
      </p>

      <h2>10. Cookies et traceurs</h2>
      <p>
        Hérita n'utilise pas de cookies publicitaires ni de cookies de mesure
        d'audience non essentiels. Seuls des cookies strictement nécessaires au
        bon fonctionnement du Service peuvent être déposés (gestion de session,
        sécurité, préférences techniques). Ces cookies ne nécessitent pas de
        consentement préalable en application des recommandations de la CNIL.
        Si Hérita venait à intégrer des outils de mesure d'audience ou
        d'analyse, un bandeau de consentement conforme serait mis en place et la
        présente politique serait mise à jour.
      </p>

      <h2>11. Mise à jour de cette politique</h2>
      <p>
        La présente politique peut être actualisée pour refléter une évolution
        du Service ou de la réglementation. La date de dernière mise à jour est
        affichée en haut de cette page. Nous vous invitons à la relire
        régulièrement.
      </p>

      <h2>12. Contact</h2>
      <p>
        Pour toute question relative à vos données personnelles ou à cette
        politique, écrivez à{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>, ou consultez
        notre <a href="/contact">page Contact</a>.
      </p>
    </LegalPageLayout>
  );
};

export default Confidentialite;
